import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './BrowseProd.css';
import placeholderShirt from '../../assets/images/placeholder-shirt.png';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getTotalStock } from '../../firebase/products';

const ITEMS_PER_PAGE = 6;
const LOW_STOCK_THRESHOLD = 10;

function getStockStatus(product) {
    if (product.inventory && typeof product.inventory === 'object' && Object.keys(product.inventory).length > 0) {
        const total = getTotalStock(product.inventory);
        if (total === null) return 'unknown';
        if (total === 0) return 'out';
        if (total <= LOW_STOCK_THRESHOLD) return 'low';
        return 'in';
    }
    if (typeof product.stockQuantity === 'number') {
        if (product.stockQuantity === 0) return 'out';
        if (product.stockQuantity <= LOW_STOCK_THRESHOLD) return 'low';
        return 'in';
    }
    return 'unknown';
}

const BrowseProducts = () => {
  const navigate    = useNavigate();
  const scrollRef   = useRef(null);          // ref for mobile scroll strip

  const [products,   setProducts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const q    = query(collection(db, 'products'), where('status', '==', 'active'));
      const snap = await getDocs(q);
      const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(fetched);
    } catch (err) {
      console.error('BrowseProducts fetch error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  /* ── Desktop pagination ── */
  const handlePrevious = () => setStartIndex(prev => Math.max(0, prev - ITEMS_PER_PAGE));
  const handleNext     = () => setStartIndex(prev => Math.min(products.length - ITEMS_PER_PAGE, prev + ITEMS_PER_PAGE));

  const visibleProducts = products.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const canGoPrevious   = startIndex > 0;
  const canGoNext       = startIndex + ITEMS_PER_PAGE < products.length;

  /* ── Helpers ── */
  const handleProductClick = (product) => {
    const stockStatus = getStockStatus(product);
    if (stockStatus === 'out') return;
    const startingPrice = product.startingPrice
      || (product.sizePrices
          ? Math.min(...Object.values(product.sizePrices).filter(v => v !== null && v !== undefined && v > 0))
          : null)
      || product.price
      || 0;

    navigate('/product-details', {
      state: {
        product: {
          id:          product.id,
          name:        product.name,
          price:       startingPrice,
          category:    product.category    || 'T-Shirt',
          subcategory: product.subcategory || '',
          brand:       product.brand       || '',
          isColored:   product.isColored   ?? false,
          image:       product.imageUrl    || (Array.isArray(product.images) && product.images[0]) || placeholderShirt,
          images:      Array.isArray(product.images) ? product.images : [],
        }
      }
    });
  };

  const getImage = (product) =>
    product.imageUrl
    || (Array.isArray(product.images) && product.images[0])
    || placeholderShirt;

  const getStartingPrice = (product) => {
    if (product.startingPrice) return product.startingPrice;
    if (product.sizePrices) {
      const prices = Object.values(product.sizePrices).filter(v => v !== null && v !== undefined && v > 0);
      if (prices.length > 0) return Math.min(...prices);
    }
    return product.price || 0;
  };

  /* ── Shared card renderer ── */
  const renderCard = (product) => {
    const stockStatus = getStockStatus(product);
    const isOutOfStock = stockStatus === 'out';

    return (
      <div
        key={product.id}
        className={`product-card${isOutOfStock ? ' product-card--oos' : ''}`}
        onClick={() => !isOutOfStock && handleProductClick(product)}
      >
        <div className="product-image-wrapper">
          <img
            src={getImage(product)}
            alt={product.name}
            onError={e => { e.target.src = placeholderShirt; }}
          />
          {isOutOfStock && <div className="product-card-oos-overlay">Out of stock</div>}
        </div>
        <div className="product-info">
          <h3 className="product-name">{product.name}</h3>
          <p className="product-price">starts at ₱{getStartingPrice(product).toLocaleString()}</p>
        </div>
      </div>
    );
  };

  return (
    <section className="browse-products">
      <h2>Browse Products</h2>

      {/* ── Loading skeleton ── */}
      {loading && (
        <div className="products-skeleton-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="products-skeleton-card" />
          ))}
        </div>
      )}

      {/* ── Error / offline state ── */}
      {!loading && error && (
        <div className="bp-error-state">
          <p className="bp-error-msg">Could not load products. Check your connection.</p>
          <button className="bp-retry-btn" onClick={fetchProducts}>Try Again</button>
        </div>
      )}

      {/* ── Normal render ── */}
      {!loading && !error && (
        <>
          {/* DESKTOP: arrows + 3×2 grid */}
          <div className="products-container bp-desktop-only">
            <button
              className="nav-arrow"
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              aria-label="Previous products"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <div className="products-grid">
              {visibleProducts.map(product => renderCard(product))}
            </div>

            <button
              className="nav-arrow"
              onClick={handleNext}
              disabled={!canGoNext}
              aria-label="Next products"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>

          {/* MOBILE: horizontal scroll strip — shows ALL products, no pagination */}
          <div className="bp-scroll-strip bp-mobile-only" ref={scrollRef}>
            {products.map(product => renderCard(product))}
          </div>
        </>
      )}
    </section>
  );
};

export default BrowseProducts;